import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  CssBaseline,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  IconButton,
  Typography,
  TextField,
} from '@mui/material';
import { createTheme, ThemeProvider, StyledEngineProvider } from '@mui/material/styles';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import DownloadIcon from '@mui/icons-material/Download';
import '../App.css';

/* ---------- THEME ---------- */
const customTheme = createTheme({
  palette: {
    primary: { main: '#ff1744', contrastText: '#ffffff' },
    background: { default: '#121212', paper: '#1e1e1e' },
  },
  typography: {
    h5: { fontWeight: 600, fontSize: '0.875rem' },
    body1: { fontSize: '0.875rem' },
  },
  components: {
    MuiInputLabel: { 
      styleOverrides: { 
        root: { 
          color: '#ffffff',
          fontSize: '0.875rem',
        } 
      } 
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          backgroundColor: '#333333',
          fontSize: '0.875rem',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#ffffff' },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ffffff',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#ffffff',
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: { 
        paper: { 
          backgroundColor: '#333333', 
          color: '#ffffff' 
        } 
      } 
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          fontSize: '0.875rem',
          '&.Mui-selected': { backgroundColor: '#444444' },
          '&.Mui-selected:hover': { backgroundColor: '#555555' },
          '&:hover': { backgroundColor: '#444444' },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: { color: '#ffffff', '&.Mui-checked': { color: '#ff1744' } },
      },
    },
  },
});

/* ---------- HELPER COMPONENTS ---------- */
const PercentageCell = ({ value, isROI = false }) => {
  if (value == null) return '-';
  const val = value * 100;
  let red, green, blue;

  if (isROI) {
    // ROI coloring (similar to EV in BetTracker)
    if (val <= -10) {
      red = 255; green = 0; blue = 0;
    } else if (val > -10 && val <= 0) {
      const t = (val + 10) / 10;
      red = 255; green = Math.round(255 * t); blue = Math.round(255 * t);
    } else if (val > 0 && val <= 10) {
      const t = val / 10;
      red = Math.round(255 * (1 - t)); green = 255; blue = Math.round(255 * (1 - t));
    } else {
      red = 0; green = 255; blue = 0;
    }
  } else {
    // CLV coloring
    if (val <= -5) {
      red = 255; green = 0; blue = 0;
    } else if (val >= 5) {
      red = 0; green = 255; blue = 0;
    } else if (val < 0) {
      const ratio = (val + 5) / 5;
      red = 255; green = Math.round(255 * ratio); blue = Math.round(255 * ratio);
    } else {
      const ratio = val / 5;
      green = 255; red = Math.round(255 * (1 - ratio)); blue = Math.round(255 * (1 - ratio));
    }
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
      <Typography sx={{ 
        color: `rgb(${red},${green},${blue})`, 
        fontSize: '0.875rem',
        fontWeight: 500
      }}>
        {val.toFixed(2)}%
      </Typography>
    </Box>
  );
};

/* ---------- MAIN COMPONENT ---------- */
export default function Results({ userName, bettingMetrics }) {
  /* ---------- STATE ---------- */
  const [pageSize, setPageSize] = useState(20);
  const [sortModel, setSortModel] = useState([{ field: 'BetCount', sort: 'desc' }]);
  const [bookFilter, setBookFilter] = useState([]);
  const [leagueFilter, setLeagueFilter] = useState([]);
  const [betTypeFilter, setBetTypeFilter] = useState([]);
  const [intervalFilter, setIntervalFilter] = useState([]);
  const [minSampleSize, setMinSampleSize] = useState(0);
  
  // Track if filters have been initialized
  const filtersInitialized = useRef(false);

  /* ---------- PROCESS DATA AND GET FILTER OPTIONS ---------- */
  const { processedMetrics, filterOptions } = useMemo(() => {
    if (!Array.isArray(bettingMetrics) || bettingMetrics.length === 0) {
      return {
        processedMetrics: [],
        filterOptions: { books: [], leagues: [], betTypes: [], intervals: [] }
      };
    }

    const books = new Set();
    const leagues = new Set();
    const betTypes = new Set();
    const intervals = new Set();

    const processed = bettingMetrics.map((metric, index) => {
      // Collect unique values for filters
      if (metric.MarketsourceName) books.add(metric.MarketsourceName);
      if (metric.LeagueName) leagues.add(metric.LeagueName);
      if (metric.BetTypeName) betTypes.add(metric.BetTypeName);
      if (metric.TrackingInterval) intervals.add(metric.TrackingInterval);

      return {
        ...metric,
        id: `${metric.MarketsourceName}-${metric.LeagueName}-${metric.BetTypeName}-${metric.TrackingInterval}-${index}`
      };
    });

    return {
      processedMetrics: processed,
      filterOptions: {
        books: Array.from(books).sort(),
        leagues: Array.from(leagues).sort(),
        betTypes: Array.from(betTypes).sort(),
        intervals: Array.from(intervals).sort()
      }
    };
  }, [bettingMetrics]);

  /* ---------- INITIALIZE FILTERS ---------- */
  useEffect(() => {
    if (!filtersInitialized.current && filterOptions.books.length > 0) {
      setBookFilter(filterOptions.books);
      setLeagueFilter(filterOptions.leagues);
      setBetTypeFilter(filterOptions.betTypes);
      setIntervalFilter(filterOptions.intervals);
      filtersInitialized.current = true;
    }
  }, [filterOptions]);

  /* ---------- FILTER HANDLERS ---------- */
  const handleFilterChange = (e, options, setter, currentValue) => {
    const val = e.target.value;
    
    // Check if 'all' was clicked
    if (val[val.length - 1] === 'all') {
      // If all are currently selected, deselect all
      if (currentValue.length === options.length) {
        setter([]);
      } else {
        // Otherwise, select all
        setter(options);
      }
    } else {
      // Normal selection - just use the selected values
      setter(val);
    }
  };

  const renderFilterValue = (selected, options, label) => {
    if (!selected.length) return label;
    if (selected.length === options.length) return 'All';
    return selected.length > 1 ? `Multiple (${selected.length})` : selected[0];
  };

  const handleMinSampleChange = (e) => {
    const value = e.target.value;
    // Only allow non-negative integers
    if (value === '' || (!isNaN(value) && parseInt(value) >= 0)) {
      setMinSampleSize(value === '' ? 0 : parseInt(value));
    }
  };

  /* ---------- APPLY FILTERS ---------- */
  const filteredMetrics = useMemo(() => {
    return processedMetrics.filter(metric => {
      // Book filter
      if (bookFilter.length > 0 && bookFilter.length < filterOptions.books.length) {
        if (!bookFilter.includes(metric.MarketsourceName)) return false;
      }
      
      // League filter
      if (leagueFilter.length > 0 && leagueFilter.length < filterOptions.leagues.length) {
        if (!leagueFilter.includes(metric.LeagueName)) return false;
      }
      
      // Bet type filter
      if (betTypeFilter.length > 0 && betTypeFilter.length < filterOptions.betTypes.length) {
        if (!betTypeFilter.includes(metric.BetTypeName)) return false;
      }
      
      // Interval filter
      if (intervalFilter.length > 0 && intervalFilter.length < filterOptions.intervals.length) {
        if (!intervalFilter.includes(metric.TrackingInterval)) return false;
      }
      
      // Minimum sample size filter
      if (minSampleSize > 0 && (metric.BetCount || 0) < minSampleSize) {
        return false;
      }
      
      return true;
    });
  }, [processedMetrics, bookFilter, leagueFilter, betTypeFilter, intervalFilter, minSampleSize, filterOptions]);

  /* ---------- EXPORT TO CSV ---------- */
  const exportToCSV = () => {
    if (!filteredMetrics.length) return;
  
    const headers = [
      'Book', 'League', 'Bet Type', 'Time to Close',
      'Avg EV (%)', 'Avg CLV (%)', 'Wins', 'Losses', 'ROI (%)', 
      'Bets'
    ];
  
    const rows = filteredMetrics.map(metric => [
      metric.MarketsourceName || '',
      metric.LeagueName || '',
      metric.BetTypeName || '',
      metric.TrackingInterval || '',
      metric.AvgROI != null ? (metric.AvgROI * 100).toFixed(2) : '',
      metric.AvgCLV != null ? (metric.AvgCLV * 100).toFixed(2) : '',
      metric.Wins || 0,
      metric.Losses || 0,
      metric.ActualROI != null ? (metric.ActualROI * 100).toFixed(2) : '',
      metric.BetCount || 0
    ].map(value => `"${String(value).replace(/"/g, '""')}"`).join(','));
  
    const csvContent = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `betting_metrics_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  /* ---------- COLUMNS ---------- */
  const columns = [
    {
      field: 'MarketsourceName',
      headerName: 'Book',
      flex: 1,
      minWidth: 70,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', gap: 1 }}>
          {params.row.logoURL ? (
            <img
              src={params.row.logoURL}
              alt={params.value || 'Sportsbook'}
              style={{ width: 72, height: 72, objectFit: 'contain' }}
              onError={(e) => {
                // Fallback to text if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'inline';
              }}
            />
          ) : null}
          <Typography 
            sx={{ 
              fontSize: '0.875rem',
              display: params.row.logoURL ? 'none' : 'inline',
            }}
          >
            {params.value || ''}
          </Typography>
        </Box>
      ),
    },

    {
      field: 'LeagueName',
      headerName: 'League',
      flex: 1,
      minWidth: 100,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'BetTypeName',
      headerName: 'Bet Type',
      flex: 1.5,
      minWidth: 150,
      headerAlign: 'left',
      align: 'left',
    },
    {
      field: 'TrackingInterval',
      headerName: 'Time to Close',
      flex: 1,
      minWidth: 100,
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '0.875rem' }}>
            {params.value?.replace('_', ' ') || ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'AvgROI',
      headerName: 'Avg EV',
      flex: 1,
      minWidth: 100,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <PercentageCell value={params.value} isROI={true} />,
    },
    {
      field: 'AvgCLV',
      headerName: 'Avg CLV',
      flex: 1,
      minWidth: 100,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <PercentageCell value={params.value} isROI={false} />,
    },

        {
      field: 'Wins',
      headerName: 'Wins',
      flex: 0.8,
      minWidth: 80,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '0.875rem' }}>
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
            {
      field: 'Losses',
      headerName: 'Losses',
      flex: 0.8,
      minWidth: 80,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '0.875rem' }}>
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'ActualROI',  // Change to the actual field name from your WebSocket data
      headerName: 'ROI',
      flex: 1,
      minWidth: 100,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => <PercentageCell value={params.value} isROI={true} />,
    },
    {
      field: 'BetCount',
      headerName: 'Bets',
      flex: 0.8,
      minWidth: 80,
      type: 'number',
      headerAlign: 'left',
      align: 'left',
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography sx={{ fontSize: '0.875rem' }}>
            {params.value || 0}
          </Typography>
        </Box>
      ),
    },
  ];

  /* ---------- RENDER ---------- */
  return (
    <ThemeProvider theme={customTheme}>
      <StyledEngineProvider injectFirst>
        <CssBaseline />
        <Box sx={{ 
          width: '100%', 
          height: '100vh', 
          backgroundColor: 'background.default', 
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden' 
        }}>
          {/* Filter Section */}
          <Box className="filter-section">
            <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
              {/* Filters */}
              <Stack direction="row" spacing={2} alignItems="center">
                {[
                  { label: 'Book', value: bookFilter, setter: setBookFilter, options: filterOptions.books },
                  { label: 'League', value: leagueFilter, setter: setLeagueFilter, options: filterOptions.leagues },
                  { label: 'Bet Type', value: betTypeFilter, setter: setBetTypeFilter, options: filterOptions.betTypes },
                  { label: 'Time to Close', value: intervalFilter, setter: setIntervalFilter, options: filterOptions.intervals },
                ].map(({ label, value, setter, options }) => (
                  <FormControl key={label} variant="outlined" size="small" sx={{ minWidth: 120 }}>
                    <InputLabel shrink sx={{ fontSize: '0.875rem' }}>
                      {label}
                    </InputLabel>
                    <Select
                      multiple
                      label={label}
                      notched
                      value={value}
                      onChange={(e) => handleFilterChange(e, options, setter, value)}
                      renderValue={(sel) => renderFilterValue(sel, options, label)}
                      sx={{ fontSize: '0.875rem' }}
                    >
                      <MenuItem value="all">
                        <Checkbox 
                          checked={value.length === options.length && options.length > 0}
                          indeterminate={value.length > 0 && value.length < options.length}
                        />
                        <ListItemText primary="Select All" />
                      </MenuItem>
                      {options.map((option) => (
                        <MenuItem key={option} value={option}>
                          <Checkbox checked={value.includes(option)} />
                          <ListItemText primary={option} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                ))}
                
                <TextField
                  value={minSampleSize}
                  onChange={handleMinSampleChange}
                  label="Min Sample"
                  size="small"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: 0, step: 1 }}
                  sx={{ 
                    width: 120,
                    '& .MuiInputBase-input': { color: 'white' },
                  }}
                />
              </Stack>

              {/* Controls */}
              <Stack direction="row" spacing={1} alignItems="center">
                <IconButton
                  onClick={exportToCSV}
                  disabled={filteredMetrics.length === 0}
                  sx={{
                    color: '#ffffff',
                    '&:hover': { backgroundColor: 'rgba(187, 187, 187, 0.1)' },
                    '&.Mui-disabled': { color: 'gray' },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Stack>
            </Stack>
          </Box>

          {/* Data Grid */}
          <Box className="table-section">
            <Box className="data-grid-container" sx={{ height: '100%', boxSizing: 'border-box' }}>
              {filteredMetrics.length === 0 ? (
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  height: '100%'
                }}>
                  <Typography sx={{ color: '#888', mb: 2 }}>
                    {bettingMetrics && bettingMetrics.length > 0 
                      ? 'No data matches your current filters'
                      : 'Loading betting metrics data...'
                    }
                  </Typography>
                </Box>
              ) : (
                <DataGridPro
                  rows={filteredMetrics}
                  columns={columns}
                  pageSize={pageSize}
                  onPageSizeChange={setPageSize}
                  rowsPerPageOptions={[20, 50, 100]}
                  pagination
                  paginationMode="client"
                  sortingMode="client"
                  sortModel={sortModel}
                  onSortModelChange={setSortModel}
                  disableSelectionOnClick
                  disableColumnFilter
                  disableColumnMenu
                  getRowId={(row) => row.id}
                  hideFooterSelectedRowCount
                  components={{ Toolbar: GridToolbar }}
                  sx={{
                    '& .MuiDataGrid-columnHeaders': { 
                      backgroundColor: '#1e1e1e', 
                      borderBottom: '2px solid #444',
                    },
                    '& .MuiDataGrid-columnHeader:focus': { outline: 'none' },
                    '& .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-columnHeaderTitle': {
                      fontSize: '0.875rem',
                      fontWeight: 700,
                      letterSpacing: '0.02rem',
                      color: '#ffffff !important',
                    },
                    '& .MuiDataGrid-cell': {
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                      borderRight: '1px solid rgba(255, 255, 255, 0.3)', // Column separator - increased opacity to 30%
                    },
                    '& .MuiDataGrid-cell:last-child': {
                      borderRight: 'none', // Remove border from last column
                    },
                    '& .MuiDataGrid-cell:focus': { outline: 'none' },
                    '& .MuiDataGrid-cell:focus-within': { outline: 'none' },
                    '& .MuiDataGrid-row:hover': { backgroundColor: '#1a1a1a' },
                    '& .MuiDataGrid-columnSeparator': { 
                      visibility: 'visible',
                      color: 'rgba(255, 255, 255, 0.3)',
                    },
                    '& .MuiDataGrid-sortIcon': { color: '#ffffff !important' },
                    '& .MuiTablePagination-root, & .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': { 
                      color: '#ffffff !important',
                      fontSize: '0.875rem',
                    },
                    '& .MuiSelect-icon': { color: '#ffffff !important' },
                    '& .MuiDataGrid-footerContainer': {
                      backgroundColor: '#1e1e1e',
                      color: '#ffffff !important',
                    },
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>
      </StyledEngineProvider>
    </ThemeProvider>
  );
}